<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

/**
 * Modal shown when the user clicks an upstream incident they cannot access.
 * Lists domains that do have access so they can request permissions.
 */
import SModal from '@/components/SModal.vue';
import SEmptyState from '@/components/SEmptyState/SEmptyState.vue';
import SButton from '@/components/SButton/SButton.vue';

interface IncidentRestrictedAccessModalProps {
  associatedDomainNames?: string[] | null;
}

const props = defineProps({
  associatedDomainNames: { default: null }
});

const modal = _ref<InstanceType<typeof SModal> | null>(null);

const domainsLabel = _computed(() => {
  const domains = props.associatedDomainNames ?? [];
  if (!domains.length) return '';
  return `${domains.join(', ')}`;
});

const openModal = (): void => {
  modal.value?.openModal();
};
const closeModal = (): void => {
  modal.value?.closeModal();
};

defineExpose({ openModal, closeModal });
</script>

<template lang="pug">
SModal(
  ref="modal"
  :title="$t('incidents.restricted_modal_title')"
  :can-save="false"
  :can-cancel="false"
  width="480"
  remove-dividers
  data-track="incident-restricted-access-modal")
  SEmptyState(
    icon="icon-lock-closed"
    size="medium"
    :title="$t('incidents.restricted_modal_access_denied_title')")
    template(#description)
      span.mt-1.grey--text.text-center.text-body-2.incident-restricted-access-modal__description
        | {{ $t('assets.access_denied_message') }}
        template(v-if="domainsLabel")
          br
          br
          | {{ $t('assets.domains_with_access', { domains: domainsLabel }) }}
  template(#actions)
    SButton(
      color="primary"
      data-track="incident-restricted-access-modal-close"
      :text="$t('common.words.close')"
      @click="closeModal")
</template>
